import { IconX } from '@tabler/icons-react-native';
import { useAtom } from 'jotai';
import { guestAppBannerDismissedAtom } from 'app/atoms/guest-session';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { CATALOG_GRID_MAX_WIDTH } from 'app/components/web/dashboard-layout-constants';
import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { usePresentAppDownloadPrompt } from 'app/hooks/use-present-app-download-prompt';
import { env } from 'app/lib/env';
import { createTheme as createDSTheme } from 'app/shopify';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { type DimensionValue } from 'react-native';

const theme = createDSTheme();

export function GuestAnnouncementBanner({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation('all-listings');
  const { t: tAccessibility } = useTranslation('accessibility');
  const [dismissed, setDismissed] = useAtom(guestAppBannerDismissedAtom);
  const { t: tCommon } = useTranslation('common');
  const presentAppDownloadPrompt = usePresentAppDownloadPrompt();

  if (dismissed) return null;

  return (
    <Row
      style={{
        alignSelf: 'center',
        alignItems: 'center',
        gap: theme.spacing['spacing.general.x2'],
        backgroundColor: '#EDF8F5',
        borderRadius: 16,
        marginVertical: theme.spacing['spacing.general.x2'],
        maxWidth: CATALOG_GRID_MAX_WIDTH,
        minHeight: 72,
        paddingHorizontal: theme.spacing['spacing.general.x4'],
        paddingVertical: theme.spacing['spacing.general.x2'],
        width: `calc(100% - ${String(theme.spacing['spacing.semantic.page-margin'] * 2)}px)` as DimensionValue,
        marginHorizontal: theme.spacing['spacing.semantic.page-margin'],
      }}
    >
      <Button
        accessibilityLabel={tAccessibility('close')}
        onPress={() => setDismissed(true)}
        variant="plain"
        height={32}
        width={32}
        testID="guest-app-banner-close"
      >
        <IconX color={theme.colors['icon.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
      </Button>
      <Button
        accessibilityLabel={t('guest.bannerCta')}
        onPress={() => presentAppDownloadPrompt({ context: 'guest' })}
        variant="plain"
        style={{ flex: 1, minWidth: 0 }}
      >
        <Row alignItems="center" width="100%" flex={1} gap="spacing.general.x3" style={{ flexWrap: 'nowrap' }}>
          <Row alignItems="center" flex={1} gap="spacing.general.x3" style={{ minWidth: 0 }}>
            <PCImage
              contentFit="cover"
              source={env.IS_PARTAKE ? images.logoThreeLinesPartake : images.guest_banner_thumbnail}
              style={{ borderRadius: 8, height: 48, width: 48 }}
            />

            <Column flex={1} gap="spacing.general.x1" style={{ minWidth: 0 }}>
              <Text color="black" variant="bodySmallMedium">
                {compact ? t('guest.mobileBannerTitle', { appName: tCommon('appName') }) : t('guest.bannerTitle')}
              </Text>
              {!compact ? (
                <Text color="text.secondary" variant="bodyXSmall">
                  {t('guest.bannerText')}
                </Text>
              ) : null}
            </Column>
          </Row>

          <Row
            alignItems="center"
            gap="spacing.general.x2"
            style={{ minWidth: compact ? 92 : 128, flexDirection: compact ? 'column-reverse' : 'row' }}
          >
            <Row alignItems="center" gap="spacing.general.x2">
              <PCImage source={images.guest_banner_apple} style={{ height: 20, width: 20 }} />
              <PCImage source={images.guest_banner_android} style={{ height: 20, width: 20 }} />
            </Row>
            <Row
              alignItems="center"
              justifyContent="center"
              style={{
                backgroundColor: '#005A5D',
                borderRadius: 1000,
                height: 32,
                minWidth: 92,
                paddingHorizontal: 12,
              }}
            >
              <Text color="white" fontSize={compact ? 12 : 14} fontWeight="600" lineHeight={17} numberOfLines={1}>
                {t('guest.bannerCta')}
              </Text>
            </Row>
          </Row>
        </Row>
      </Button>
    </Row>
  );
}
